import classNames from 'classnames';
import React from 'react';
import { FormRow, TextInput } from '~/components';
import { ShiftDifferentialType } from '~/types/hlx/enums';

export const SHIFT_DIFF_VALUE_LABELS: Record<ShiftDifferentialType, string> = {
  INCR_HOURLY_RATE: 'increment ($/hr)',
  MULTIPLIER: 'multiplier',
  RVU: 'rate ($/RVU)',
  HOURLY_RATE: 'rate ($/hr)',
};

const LabelBox = ({ label, color }: { label: string; color?: 'blue' | 'grey' }) => {
  const colorToUse = color || 'blue';
  return (
    <div
      className={`w-full ${colorToUse === 'blue' ? 'bg-primary-t-900' : 'bg-grey-900'} p-4 rounded-lg`}
    >
      <p className="body-text-sb">{label}</p>
    </div>
  );
};

export const ShiftTableForm = ({
  inputFields,
  tags,
  defaultVal,
  withOwnMargin = true,
  valueLabel = 'shifts',
}: {
  inputFields: string[];
  tags: { [key: string]: string };
  defaultVal?: string;
  valueLabel?: string;
  /**
   * Keep the table's own `mb-4`.
   *
   * [MCV-1577] Same flag, and for the same reason, as `InfoMessage`: inside a
   * `FormLayout` the margin adds to the 20px `gap-5` instead of collapsing
   * into it, and it has to be removed rather than overridden because there is
   * no tailwind-merge to make a passed `mb-0` beat it. Default left on for any
   * consumer outside a gap-owning parent.
   */
  withOwnMargin?: boolean;
}) => {
  return (
    <div
      className={classNames(
        'grid min-w-0 grid-cols-1 items-start gap-2 text-grey-100 sm:grid-cols-4',
        {
          'mb-4': withOwnMargin,
        }
      )}
    >
      <div className="hidden sm:block" />
      {['Day', 'Evening', 'Night'].map((shift) => (
        <div key={shift} className="hidden sm:block">
          <LabelBox label={shift} />
        </div>
      ))}
      {inputFields.map((field, index) => (
        <React.Fragment key={field || index}>
          {index % 3 === 0 && (
            <LabelBox
              color="grey"
              label={index === 0 ? 'Weekday' : index === 3 ? 'Weekend' : 'Holiday'}
            />
          )}
          {field === '' ? (
            <div className="min-w-0">
              <p className="body-text-sb mb-2 sm:hidden">Day {valueLabel}</p>
              <div className="w-full bg-grey-900 p-4 rounded-lg">
                <p className="body-text-sb">{defaultVal}</p>
              </div>
            </div>
          ) : (
            <FormRow>
              <label htmlFor={field} className="body-text-sb sm:sr-only">
                <span className="sr-only">
                  {index < 3 ? 'Weekday' : index < 6 ? 'Weekend' : 'Holiday'}{' '}
                </span>
                {['Day', 'Evening', 'Night'][index % 3]} {valueLabel}
              </label>
              <TextInput name={field} cyTag={tags[field]} type="number" />
            </FormRow>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
